import _find from 'lodash/find';
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import Analytics from '@web-solutions/module-analytics';
import { SOLIDGATE_ERROR_CODES } from '@web-solutions/core/constants/errors';
import type { ImageSource } from '@web-solutions/core/interfaces/images';
import { getProductsTexts } from '@web-solutions/core/utils/products';
import { tKey as specialOfferTKey } from 'core/summary/special-offer/constants';
import {
  ButtonPlacementType,
  ButtonBehavior,
  ButtonConfig,
  InterButtonConfig,
} from 'core/payment/constants';
import { scrollToSection } from 'core/utils/scroll-to-plans';
import {
  applePayOrder,
  createCustomer,
  handleErrorPurchase,
  init,
  initNoFundsOfferEndDate,
  initOrders,
  initSpecialOfferEndDate,
  subscribe,
  switchToReservePaymentSystem,
} from 'core/store/billing/actions';
import {
  ProductDetails,
  selectProducts,
  selectLoading,
  selectSpecialOfferEndDate,
  selectNoFundsOfferEndDate,
  selectSpecialOfferProducts,
  selectPaymentSystem,
  selectInjectedPaymentMethod,
} from 'core/store/billing/selectors';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { InertialApplePay } from 'core/constants/billing';
import { useShowApplePay, useShowInertialApplePay } from 'core/hooks/use-show-payment-method';
import { useSummaryViewedRate } from 'core/hooks/use-summary-viewed-rate';
import type { PaymentModalTitle } from 'core/interfaces/payment-modal';
import { Preloader } from 'core/ui-elements';
import { PaymentProcessor, PaymentProcessorRef } from 'core/payment';
import { getPaymentErrorAnalyticsParams } from 'core/payment/utils/payment-error-state';
import classes from './style.module.scss';
import { CoreSummarySection } from 'core/summary/types';
import { Plans } from 'core/summary/plans';
import {
  SpecialOfferInfo,
  SpecialOfferNoFunds,
  SpecialOfferWithPlans,
} from 'core/summary/special-offer';
import { FixedButton } from 'core/summary/fixed-button';
import { TrialPrice } from 'core/summary/trial-price';
import { DownSaleOfferModal } from 'core/summary/down-sale-modal-offer';
import { useAppSelector } from '@web-solutions/store/types';
import { useStore } from 'src/onboarding/store/store';
import axios from 'axios';
import { debounce } from 'lodash';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { useNav } from 'src/onboarding/hooks/nav';
import { Allergies, UpdateUserRequest } from 'src/onboarding/types/api';
import { getEarlyAchievableOffset } from 'src/onboarding/helpers/getEarlyAchievableOffset';
import { getAdjustedDate } from 'src/onboarding/helpers/getAdjustedDate';
import { applyFoodzeeAuthTokens } from 'src/onboarding/helpers/applyFoodzeeAuthTokens';

import ApplePay from 'core/payment/components/apple-pay';

import { processEmail } from 'src/store/profile/actions';
import { ApplePayButton, PaymentSystem, Provider } from '@web-solutions/react-billing';
import { PaymentError } from 'core/interfaces/errors';
import { Subscription } from 'core/interfaces/billing';
import { InjectedPaymentMethod } from 'core/constants/remote-config';
import { t } from '@web-solutions/module-localization';
import { EmailPopup } from 'src/onboarding/components/ui/email-popup/EmailPopup';
import { UserData } from '@web-solutions/base-app/api/soula-api/types';
import { createUser } from '@web-solutions/base-app/api/soula-api';

declare const gtag: Function;

const usersPercentToActivateScrollMetric =
  process.env.REACT_APP_USERS_PERCENT_TO_ACTIVATE_SCROLL_METRIC;
const tKeyPlans = 'core.plans';

export interface ProductsTexts {
  titleText: string;
  subTitleText: string;
  perText: string;
  perTitle: string;
  id: string;
}

export interface SummaryProps {
  landingStack?: string[];
  isApiReady?: boolean;
  isLoadingApp?: boolean;
  sections?: { [key: string]: React.FC };
  landingButtons?: InterButtonConfig[];
  buttonsConfigs?: { [key in ButtonPlacementType]: ButtonConfig };
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  plansSubtitle?: React.ReactNode;
  paymentModalTitle?: PaymentModalTitle;
  paymentModalSubtitle?: React.ReactNode;
  soInfoImage?: ImageSource;
  // onSuccess?: (purchase?: any) => void,
  onNextClick?: () => void;
  isPurchaseProgress?: boolean;
  showDownSaleModalOnce: boolean;
  setShowDownSaleModalOnce: (param: boolean) => void;
  scrollToTarget?: () => void;
  hasTryFoodzeeSection?: boolean;
}

interface PlansRef {
  onPayment: () => void;
}

const PersonalPlans = forwardRef<PlansRef, SummaryProps>((props, ref) => {
  const {
    header,
    footer,
    className,
    plansSubtitle,
    paymentModalTitle,
    paymentModalSubtitle,
    soInfoImage,
    onNextClick,
    isPurchaseProgress = false,
    showDownSaleModalOnce,
    setShowDownSaleModalOnce,
    scrollToTarget,
    hasTryFoodzeeSection,
  } = props;

  const dispatch = useDispatch();
  const isApiReady = useAppSelector((state) => state.profile.authorized);
  const isLoadingApp = useAppSelector((state) => !state.app.loaded);
  const ppRef = useRef<PaymentProcessorRef>(null);
  const setPage = useStore((state) => state.setPage);
  const userId = useStore((state) => state.userId);
  const trialPrice = useStore((state) => state.trialPrice);
  const startTime = useRef(performance.now());
  const [lcp, setLcp] = useState<number | null>(null);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [pendingApplePay, setPendingApplePay] = useState(false);

  const state = useStore((state) => state);
  const {
    email,
    born: age,
    gender,
    preferences: dietary,
    height,
    weight,
    idealWeight,
    when,
    exclude,
    goForWalks,
    name: userName,
    breakfast,
    snacks,
    imageUrl,
    pageAnswers,
  } = state;

  //@ts-ignore
  const emailSelector = useSelector((state) => state.profile.email);
  const selectedAnswers = useAppSelector((state) => state.quiz.selectedAnswers);

  const { onNext } = useNav();

  useEffect(() => {
    const observer = new PerformanceObserver((list) => {
      const entries: any = list.getEntries();
      setLcp(entries?.[0].renderTime);
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onPayment() {
      handleButtonClick(ButtonPlacementType.PLANS);
    },
  }));

  const {
    openPaymentModalOnProductClick,
    inertialApplePay,
    quizPaymentModalFullMode,
    isPaymentModalShowAfterQuiz,
    isInjectedPaymentMethodOnPage,
    specialOffer,
    noFundsOffer,
    //@ts-ignore
    landingStack,
    buttonsConfigs,
    showSpecialOffer,
    routeGroups: flow,
    showPlans,
    scrollEnabled,
    sendEthnicity,
    redirectToPwa,
    paywall,
    errorPopup,
    paymentSystemReserve,
    //@ts-ignore
    collectEmailAfterPayment,
  } = useRemoteConfig();

  const isLoadingBilling = useSelector(selectLoading);
  const products = useSelector(selectProducts);
  const specialOfferEndDate = useSelector(selectSpecialOfferEndDate);
  const noFundsOfferEndDate = useSelector(selectNoFundsOfferEndDate);

  const setShowDownsale = useStore((state) => state.setShowDownsale);

  const showApplePay = useShowApplePay();
  const paymentSystem = useSelector(selectPaymentSystem);

  const injectedPaymentMethod = useSelector(selectInjectedPaymentMethod);

  const injectedApplePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL;

  useEffect(() => {
    if (products.length > 0 && lcp) {
      const endTime = performance.now();
      const productsLoading = `${((endTime - startTime.current) / 1000).toFixed(2)} sec`;
      Analytics.trackEvent(`lcp`, 'metric', {
        lcp: `${(lcp / 1000).toFixed(2)} sec`,
        productsLoading,
      });
    }
  }, [products.length, lcp]);

  //@ts-ignore
  const orderDetails = useSelector((state) => state.billing.orderDetails);
  //@ts-ignore
  const isLoaded = useSelector((state) => !state.billing.loading);
  const [selectedSpecialOfferProduct, setSelectedSpecialOfferProduct] = useState(null);
  const specialOfferProducts = useSelector(selectSpecialOfferProducts);
  //@ts-ignore
  const { title, highlightWords } = useSelector((state) => state.remoteConfig.specialOffer);
  const { specialOffer: offer } = useRemoteConfig();

  useEffect(() => {
    if (specialOfferProducts) {
      //@ts-ignore
      setSelectedSpecialOfferProduct(specialOfferProducts[0]);
    }
  }, [specialOfferProducts, isLoaded]);

  const selectedProduct = products.find((product) => product.subTitle.en === trialPrice);

  const shouldUseSelectedProduct =
    !!trialPrice && flow.flat().includes('trial-price') && selectedProduct;

  const [activeProduct, setActiveProduct] = useState<any>(
    shouldUseSelectedProduct
      ? selectedProduct
      : products.find((p) => p.default) || products[0] || {}
  );
  const [countPPClosed, setCountPPClose] = useState(0);
  const [soOpen, setSOOpen] = useState(false);
  const [nfoOpen, setNFOOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [tokenThreeDSecure, setTokenThreeDSecure] = useState(null);

  const { apiBaseUrl } = getEnvUrl();

  const isShowInertialApplePay = useShowInertialApplePay();
  const isSummaryReady = !isLoadingApp && !isLoadingBilling;

  useSummaryViewedRate(usersPercentToActivateScrollMetric, isSummaryReady);

  const debouncedTrackEvent = useCallback(
    debounce((products) => {
      Analytics.trackEvent(`products`, 'empty', { products });
    }, 2000),
    []
  );

  useEffect(() => {
    if (products?.length === 0) {
      debouncedTrackEvent(products);
    }

    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [products, debouncedTrackEvent]);

  useEffect(() => {
    if (!showDownSaleModalOnce) {
      //@ts-ignore
      setActiveProduct(
        shouldUseSelectedProduct
          ? selectedProduct
          : //@ts-ignore
            products[0] || activeProduct
      );
    } else {
      //@ts-ignore
      setActiveProduct(
        specialOfferProducts.find((p) => Object.values(p).includes(activeProduct?.id)) ||
          specialOfferProducts.find((p) => p.default) ||
          specialOfferProducts[0] ||
          activeProduct
      );
    }
  }, [
    activeProduct,
    products,
    setActiveProduct,
    showDownSaleModalOnce,
    specialOfferProducts,
    shouldUseSelectedProduct,
  ]);

  useEffect(() => {
    if (isApiReady) {
      dispatch(init());
    }
  }, [dispatch, isApiReady]);

  const handleProductButtonClick = useCallback(() => {
    if (isShowInertialApplePay && !isInjectedPaymentMethodOnPage) {
      const b = (document.querySelector('apple-pay-button') ||
        document.querySelector('#apple-pay') ||
        document.querySelector('button[label="apple"]')) as HTMLButtonElement | null;
      if (b) {
        try {
          Analytics.trackEvent('apple_pay', 'inertial');
          b.click();
          if (inertialApplePay !== InertialApplePay.ENABLED_WITH_DEFAULT) {
            return;
          }
        } catch (ex) {}
      }
    }

    setSOOpen(false);
    ppRef.current?.showPaymentPopup();
  }, [inertialApplePay, isInjectedPaymentMethodOnPage, isShowInertialApplePay]);

  const handleButtonClick = useCallback(
    (type?: ButtonPlacementType, forceOpen?: boolean) => {
      const behavior = type ? buttonsConfigs[type]?.behavior : undefined;

      if (type === ButtonPlacementType.PLANS) {
        handleProductButtonClick();
      } else if (!landingStack.includes(CoreSummarySection.PLANS) && onNextClick) {
        onNextClick();
      } else if (behavior === ButtonBehavior.NEXT && onNextClick) {
        onNextClick();
      } else if (behavior === ButtonBehavior.SCROLL && !forceOpen) {
        scrollToSection('#plans');
      } else {
        handleProductButtonClick();
      }
    },
    [buttonsConfigs, handleProductButtonClick, onNextClick, landingStack]
  );

  const handleInterButtonClick = useCallback(
    (config: InterButtonConfig) => {
      const behavior = config.behavior;

      if (
        (!landingStack.includes(CoreSummarySection.PLANS) || behavior === ButtonBehavior.NEXT) &&
        onNextClick
      ) {
        onNextClick();
      } else if (
        behavior === ButtonBehavior.SCROLL &&
        landingStack.includes(CoreSummarySection.PLANS)
      ) {
        scrollToSection('#plans');
      } else {
        handleProductButtonClick();
      }
    },
    [handleProductButtonClick, onNextClick, landingStack]
  );

  const handleProductClick = useCallback(
    (product: ProductDetails) => {
      setActiveProduct(product);
      if (showDownSaleModalOnce) {
        setActiveProduct(product);
      } else {
        setActiveProduct(product);
      }

      if (openPaymentModalOnProductClick) {
        Analytics.trackEvent('product', 'click', { selectedProductId: product.id });
        handleButtonClick(ButtonPlacementType.PLANS);
      }
    },
    [activeProduct, openPaymentModalOnProductClick, handleButtonClick, setActiveProduct]
  );

  const handlePPClose = useCallback(() => {
    setCountPPClose((p) => p + 1);
    if (!showDownSaleModalOnce) {
      handleShowDownSaleModal();
    }
  }, [specialOfferProducts, selectedSpecialOfferProduct, showDownSaleModalOnce]);

  const handleOfferTriggered = useCallback(
    (type: 'specialOffer' | 'noFundsOffer') => {
      specialOffer.scrollToTopAfterTrigger && window.scrollTo({ top: 0, behavior: 'smooth' });

      switch (type) {
        case 'noFundsOffer': {
          dispatch(initNoFundsOfferEndDate());
          setNFOOpen(true);

          break;
        }
        case 'specialOffer': {
          dispatch(initSpecialOfferEndDate());
          setSOOpen(true);

          break;
        }
      }
    },
    [specialOffer.scrollToTopAfterTrigger, dispatch]
  );

  useEffect(() => {
    const isSOTriggered =
      !specialOfferEndDate &&
      specialOffer.enabled &&
      specialOffer.showAfterCloseAmount === countPPClosed;

    if (isSOTriggered) {
      handleOfferTriggered('specialOffer');
    }
  }, [
    countPPClosed,
    specialOffer.enabled,
    specialOffer.showAfterCloseAmount,
    specialOfferEndDate,
    handleOfferTriggered,
  ]);

  const handleSOClose = useCallback(() => {
    setSOOpen(false);
  }, []);

  const handleNFOClose = useCallback(() => {
    setNFOOpen(false);
  }, []);

  const handlePaymentError = useCallback(
    (error: any) => {
      // Drop any stashed Apple Pay email on failure so a later card payment
      // doesn't wrongly skip the email-after-payment page with a stale value.
      (window as any).__apple_pay_payer_email__ = undefined;

      const isSONoFundsTriggered =
        error?.code === SOLIDGATE_ERROR_CODES.INSUFFICIENT_FUNDS &&
        noFundsOffer.enabled &&
        !noFundsOfferEndDate &&
        error?.entity === 'form';

      if (!activeProduct?.isOneTimePurchase && isSONoFundsTriggered) {
        handleOfferTriggered('noFundsOffer');
      }
    },
    [
      activeProduct?.isOneTimePurchase,
      noFundsOffer.enabled,
      noFundsOfferEndDate,
      handleOfferTriggered,
    ]
  );

  const handleInjectedSubmit = useCallback((data: any) => {
    ppRef.current?.processFormData(data);
  }, []);

  const handleSubscribeOnOneClick = useCallback((productId: string) => {
    ppRef.current?.subscribeOnOneClick({ productId });

    setNFOOpen(false);
  }, []);

  const isShowPopUpComponent = isLoaded && selectedSpecialOfferProduct;
  const downSaleModalRef = useRef(null);

  const handleShowDownSaleModal = useCallback(() => {
    // if (showSpecialOffer) {
    //   if (specialOffer.enabled && !showDownSaleModalOnce) {
    //     setTimeout(() => {
    //       if (selectedSpecialOfferProduct) {
    //         setActiveProduct(selectedSpecialOfferProduct);
    //       }
    //       Analytics.trackEvent('special_offer_modal', 'open');
    //       downSaleModalRef.current.showPaymentPopup();
    //       // document.body.style.overflow = 'hidden';
    //       setShowDownSaleModalOnce(true);
    //       setShowDownsale(true);
    //     }, 500);
    //   }
    // }
  }, [showDownSaleModalOnce, selectedSpecialOfferProduct, showSpecialOffer]);

  const ACTIVE_SHOW_PRODUCTS = showDownSaleModalOnce
    ? specialOfferProducts
    : shouldUseSelectedProduct
      ? [selectedProduct]
      : products;

  const renderSections = () => (
    <Plans
      subtitle={plansSubtitle}
      activeProduct={activeProduct}
      products={showPlans ? ACTIVE_SHOW_PRODUCTS : undefined}
      onProductClick={showPlans ? handleProductClick : undefined}
      onFixedButtonClick={
        hasTryFoodzeeSection && scrollEnabled ? scrollToTarget : handleButtonClick
      }
      onPayPalSubmit={handleInjectedSubmit}
      productsTexts={getProductsTexts(products, tKeyPlans)}
    />
  );

  const renderSpecialOffer = () => {
    switch (specialOffer.mode) {
      case 'with_plans':
      case 'with_plans_v2':
        return (
          <SpecialOfferWithPlans
            isOpen={soOpen}
            activeProduct={activeProduct}
            onProductClick={handleProductClick}
            onGetClick={handleProductButtonClick}
            onClose={handleSOClose}
            productsTexts={getProductsTexts(products, specialOfferTKey)}
          />
        );
      case 'info':
        return (
          <SpecialOfferInfo
            isOpen={soOpen}
            offerText={specialOffer?.soInfoOfferText}
            image={soInfoImage}
            onGetClick={handleSOClose}
            onClose={handleSOClose}
          />
        );
      default:
        return null;
    }
  };

  const adjustedDate = getAdjustedDate(when, getEarlyAchievableOffset(when));

  const showNewTargetDate =
    getEarlyAchievableOffset(when) !== null && flow.flat().includes('goal-2');

  const onSuccess = async () => {
    // Deferred-email flow + Apple Pay: if the sheet returned an email, use it for
    // the user-update attach and skip the email-after-payment page. The stash is set
    // ONLY on an Apple Pay authorization and cleared on payment error, so a card
    // payment never sees it (card always goes through the email-after-payment page).
    //
    // Not restricted to Stripe: TailoredPay's Apple Pay fills the same stash (from
    // the card form's wallet handler), and it only ever holds an email when
    // `requestApplePayEmail` asked the sheet for one — so no extra flag check here.
    const applePayEmail = collectEmailAfterPayment
      ? (window as any).__apple_pay_payer_email__
      : undefined;
    const isValidApplePayEmail =
      typeof applePayEmail === 'string' && /\S+@\S+\.\S+/.test(applePayEmail);
    const finalEmail = email || (isValidApplePayEmail ? applePayEmail : '');
    if (isValidApplePayEmail) {
      useStore.getState().setEmail(applePayEmail);
      useStore.getState().addSkippedPage('email-after-payment');
    }
    (window as any).__apple_pay_payer_email__ = undefined;
    try {
      gtag('event', 'conversion', {
        send_to: 'AW-17149973324/2M00CM_qt_0aEMym3_E_',
        value: 15.0,
        currency: 'USD',
        transaction_id: '',
      });

      const ethnicityAnswers = Object.entries(pageAnswers).map(([key, value]) => {
        const questionText =
          //@ts-ignore
          (value.title && value.title.en) ||
          //@ts-ignore
          (typeof value.title === 'string' ? value.title : '');

        return {
          question: questionText,
          //@ts-ignore
          answer: Array.isArray(value.answer) ? value.answer.join(', ') : value.answer,
        };
      });

      const userData: UpdateUserRequest = {
        email: finalEmail,
        subscription: {
          isActive: true,
        },
        ...(age && { age }),
        gender,
        activity_level: goForWalks || 'Active',
        dietary: dietary || 'No any preferences',
        allergies:
          exclude && exclude.length > 0
            ? (Object.fromEntries(
                ['no_allergies', 'gluten', 'wheat', 'lactose', 'nuts', 'eggs', 'fish', 'meat'].map(
                  (allergy) => [allergy, exclude.includes(allergy)]
                )
              ) as Allergies)
            : {
                no_allergies: true,
                gluten: false,
                wheat: false,
                lactose: false,
                nuts: false,
                eggs: false,
                fish: false,
                meat: false,
              },
        height:
          height.type === 'ft'
            ? {
                ft: height.ft,
                type: 'ft',
              }
            : {
                cm: Number(height.cm),
                type: 'cm',
              },
        weight: {
          kg: weight.type === 'kg' ? Number(weight.kg) : undefined,
          lbs: weight.type === 'lbs' ? Number(weight.lbs) : undefined,
          type: weight.type,
        },
        targetWeight: idealWeight.value
          ? {
              kg: idealWeight.type === 'kg' ? Number(idealWeight.value) : undefined,
              lbs: idealWeight.type === 'lbs' ? Number(idealWeight.value) : undefined,
              type: idealWeight.type,
            }
          : {
              kg: weight.type === 'kg' ? Number(weight.kg) : undefined,
              lbs: weight.type === 'lbs' ? Number(weight.lbs) : undefined,
              type: weight.type,
            },
        targetWeightDate: (() => {
          if (showNewTargetDate) {
            return new Date(adjustedDate.year, +adjustedDate.month);
          }

          const flowPages = flow.flat();
          const hasWhenPage = flowPages.some((page) => page.toLowerCase().includes('when'));

          if (!hasWhenPage) {
            const oneYearLater = new Date();
            oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
            return oneYearLater;
          }

          const whenDate = new Date(+when.year, +when.month - 1, +when.day);

          return whenDate;
        })(),
        name: userName,
        preferredDailyMeals: {
          breakfast: breakfast === false ? false : true,
          snack: snacks === false ? false : true,
          lunch: true,
          dinner: true,
        },
        ...(() => {
          if (sendEthnicity === 'onPersonalPlan') {
            if (ethnicityAnswers.length > 0) {
              return {
                ethnicity: {
                  data: ethnicityAnswers,
                  ...(imageUrl && { photoUrl: imageUrl }),
                  appName: "ravelo"
                },
              };
            }
          }
          return { ethnicity: undefined };
        })(),
        responseWithAuthTokens: true,
      };

      // Deferred-email flow: update here ONLY when the email-after-payment page is
      // skipped, i.e. Apple Pay returned a valid email. For card (and Apple Pay
      // without email) the page is shown and IT sends the full update (profile +
      // subscription + email) after the user enters their email. Non-deferred flow
      // always updates here (email is already known from the email page).
      if (isValidApplePayEmail || !collectEmailAfterPayment) {
        const response = await axios.put(`${apiBaseUrl}/user/update-by-token/${userId}`, userData);
        Analytics.trackEvent('user-update-token', 'success');
        const { refreshToken, accessToken } = response.data;

        // `update-by-token` rotates the tokens, so the new pair has to reach the API
        // clients too — not just the store. `setAuthTokens` alone would leave
        // `Billing`/axios and redux holding the previous access token, which the
        // server has just replaced: every following /nmi request 401s, billing init
        // fails and the funnel silently drops onto the reserve payment system.
        applyFoodzeeAuthTokens(
          { accessToken, refreshToken, uid: userId },
          dispatch,
          { paymentSystem },
        );
      }
    } catch (error) {
      Analytics.trackEvent('user-update-token', 'error');
      console.error('Error:', error);
    }
    onNext();
  };

  const handlePaymentSuccess = useCallback(
    async (purchase: Subscription) => {
      Analytics.trackEvent('modal_payment', 'success');
      //@ts-ignore
      onSuccess(purchase);
    },
    [onSuccess]
  );
  const [errorCode, setErrorCode] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isOpenErrorPopup, setIsOpenErrorPopup] = useState(false);

  const handleCardPaymentError = useCallback(
    // `method` comes from the form data at the call site — this layout keeps the
    // submitted method in state, which is a tick behind by the time the charge
    // rejects, so the event would report the previous attempt's method.
    (error: PaymentError, method?: string) => {
      Analytics.trackEvent(
        'modal_payment',
        'error',
        getPaymentErrorAnalyticsParams(error, {
          paymentSystem: error?.paymentSystem || paymentSystem,
          method,
          place: 'purchase',
        }),
      );

      // Our side is at fault (gateway not configured, SDK never loaded), so there
      // is nothing for the user to fix. Switch to the reserve payment system
      // WITHOUT the error popup — swapping providers must be invisible.
      if ((error as any)?.isConfigurationError) {
        dispatch(handleErrorPurchase(error, { noToast: true }));
        dispatch(switchToReservePaymentSystem());
        handlePaymentError(error);
        return;
      }

      const isInsufficientFundsErrorCode = error?.code === SOLIDGATE_ERROR_CODES.INSUFFICIENT_FUNDS;
      const isSONoFundsTriggered =
        isInsufficientFundsErrorCode &&
        noFundsOffer.enabled &&
        !noFundsOfferEndDate &&
        error?.entity === 'form';

      // Swapping the payment provider must be invisible: the user did nothing
      // wrong, so a "billing issue / update your card" popup would be misleading.
      // Retry silently on the reserve, and keep the popup only for when there is
      // nowhere left to move (no reserve configured, or we are already on it).
      const currentPaymentSystem = error?.paymentSystem || paymentSystem;
      const willSwitchToReserve =
        !isInsufficientFundsErrorCode &&
        !!paymentSystemReserve &&
        paymentSystemReserve !== currentPaymentSystem;

      if (willSwitchToReserve) {
        dispatch(handleErrorPurchase(error, { noToast: true }));
        dispatch(switchToReservePaymentSystem());
        handlePaymentError(error);
        return;
      }

      let noToast;
      setErrorCode(error?.code);
      setErrorMessage(error?.message);
      if (!activeProduct?.isOneTimePurchase && errorPopup?.enabled) {
        Analytics.trackEvent('error_popup', 'open', {
          message: error?.message,
          paymentSystem: currentPaymentSystem,
        });
        setIsOpenErrorPopup(true);
        noToast = true;
      }
      dispatch(handleErrorPurchase(error, { noToast }));
      if (error?.paymentSystem === PaymentSystem.SOLIDGATE) {
        dispatch(initOrders());
      }

      handlePaymentError(error);
    },
    [
      noFundsOffer.enabled,
      noFundsOfferEndDate,
      activeProduct?.isOneTimePurchase,
      errorPopup?.enabled,
      dispatch,
      handlePaymentError,
      paymentSystem,
      paymentSystemReserve,
    ]
  );

  const handleRequireEmail = () => {
    setIsEmailPopupOpen(true);
    setPendingApplePay(true);
  };

  const processFormData = useCallback(
    async (formData: any) => {
      Analytics.trackEvent('modal_payment', 'submit', {
        method: formData?.method,
        paymentSystem: formData?.paymentSystem,
        productId: activeProduct?.id,
        value: +activeProduct?.trialPriceAmount || +activeProduct?.amount,
        currency: activeProduct?.currency,
      });
      setPaymentData(formData);

      const userData: UserData = {
        email: formData?.email! || email,
        first_name: formData?.first_name,
        last_name: formData?.last_name,
        settings: {
          paid_status: activeProduct.paid_status!,
          web_questions_answers: selectedAnswers,
        },
      };

      dispatch(subscribe(formData))
        //@ts-ignore
        .then((purchase: Subscription) => {
          handlePaymentSuccess(purchase);
          APP_NAME === 'soula' && createUser(userData);
        })
        .catch((error: PaymentError & { data?: any }) => {
          if (error?.data?.three_d_secure_action_token_id) {
            setTokenThreeDSecure(error?.data?.three_d_secure_action_token_id);
          } else {
            handleCardPaymentError(error, formData?.method);
          }
        });
    },
    [
      activeProduct?.amount,
      activeProduct?.currency,
      activeProduct?.id,
      activeProduct?.trialPriceAmount,
      dispatch,
      handleCardPaymentError,
      handlePaymentSuccess,
    ]
  );

  const handleSubmit = useCallback(
    async (formData: any) => {
      const ps = formData.paymentSystem || paymentSystem;
      if (formData.email && formData.email !== email) {
        dispatch(processEmail(formData.email));
      }
      if (formData.email || formData.first_name || formData.last_name) {
        Analytics.trackEvent('user', 'info', {
          email: formData.email || undefined,
          first_name: formData.first_name || undefined,
          last_name: formData.last_name || undefined,
        });
      }
      await dispatch(createCustomer({ email: formData.email, paymentSystem: ps }));
      if (ps !== PaymentSystem.SOLIDGATE) {
        await processFormData(formData);
      }
      // onSuccess();
    },
    [
      activeProduct?.amount,
      activeProduct?.currency,
      activeProduct?.id,
      activeProduct?.trialPriceAmount,
      dispatch,
      email,
      onSuccess,
      paymentSystem,
      processFormData,
    ]
  );

  const buttonRef = useRef<HTMLButtonElement>(null);

  const onApplePayClick = useCallback(() => {
    Analytics.trackEvent('apple_pay', 'click');
  }, []);

  useEffect(() => {
    if (pendingApplePay && emailSelector) {
      setPendingApplePay(false);
      // Небольшая задержка чтобы дать React перерендерить кнопку с новым email prop
      setTimeout(() => {
        buttonRef.current?.click();
      }, 100);
    }
  }, [pendingApplePay, emailSelector]);

  const handleEmailPopupClose = useCallback(() => {
    setIsEmailPopupOpen(false);
  }, []);

  const handleApplePaySubmit = useCallback(
    async (formData: any) => {
      Analytics.trackEvent('apple_pay', 'submit');
      await handleSubmit(formData);
      if (process.env.REACT_APP_APPLE_PAY_H2H) {
        const ps = formData.paymentSystem || paymentSystem;
        if (ps === PaymentSystem.SOLIDGATE) {
          dispatch(applePayOrder(formData))
            //@ts-ignore
            .then((purchase: Subscription) => {
              handlePaymentSuccess(purchase);
            })
            .catch((error: PaymentError) => {
              handleCardPaymentError(error, formData?.method || 'applepay');
            });
        }
      }
    },
    [dispatch, handleCardPaymentError, handlePaymentSuccess, handleSubmit, paymentSystem]
  );

  const APP_NAME = t('core.appName');

  return (
    <div className={classNames(classes.wrap, className)}>
      {/* {!isSummaryReady ? (
        <Preloader />
      ) : (
        <> */}
      {header}

      {/* {renderSections()} */}
      {showApplePay ? (
        <Provider>
          <ApplePay
            activeProduct={activeProduct}
            onRequireEmail={handleRequireEmail}
            className={classes.appleButton}
            onApplePayClick={onApplePayClick}
            onApplePaySubmit={handleApplePaySubmit}
          />
        </Provider>
      ) : (
        renderSections()
      )}

      <EmailPopup isOpen={isEmailPopupOpen} onClose={handleEmailPopupClose} />

      {!landingStack.includes(CoreSummarySection.PLANS) && (
        <FixedButton
          activeProduct={activeProduct}
          buttonsConfigs={buttonsConfigs}
          onlyFixed
          onClick={handleButtonClick}
          classStyle='style'
        />
      )}

      {footer}
      {/* </>
      // )} */}
      {!!landingStack.includes(CoreSummarySection.PLANS) && (
        <PaymentProcessor
          ref={ppRef}
          paymentModalTitle={paymentModalTitle}
          paymentModalSubtitle={paymentModalSubtitle}
          activeProduct={activeProduct}
          orderDetails={orderDetails}
          fullMode={quizPaymentModalFullMode}
          shownByDefault={isPaymentModalShowAfterQuiz}
          onSuccess={onSuccess}
          onError={handlePaymentError}
          onClose={handlePPClose}
          isPurchaseProgress={isPurchaseProgress}
          showModalOnce={showDownSaleModalOnce}
        />
      )}

      {specialOffer.enabled && renderSpecialOffer()}
      {noFundsOffer.enabled && (
        <SpecialOfferNoFunds
          isOpen={nfoOpen}
          activeProductIndex={products.findIndex(({ id }) => id === activeProduct.id) || 0}
          discount={noFundsOffer.discount}
          buttonTitle={noFundsOffer?.buttonTitle}
          currentPriceText={noFundsOffer?.currentPriceText}
          onGetClick={handleSubscribeOnOneClick}
          onClose={handleNFOClose}
        />
      )}
      <TrialPrice ready={isSummaryReady} />
      {isShowPopUpComponent && (
        <DownSaleOfferModal
          //@ts-ignore
          activeProduct={activeProduct}
          products={specialOfferProducts}
          showTerms={true}
          showButton={true}
          onProductClick={handleProductClick}
          onButtonClick={handleButtonClick}
          onPayPalSubmit={handleButtonClick}
          // title={title}
          title={offer.title}
          highlightWords={highlightWords}
          ref={downSaleModalRef}
        />
      )}
    </div>
  );
});

export default React.memo(PersonalPlans);
